import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { mapPlaylistFromVideoSearch, LoadingSpinner, Dropdown, Button } from 'yankees-react-utils';
import { useOktaQueries, Player, PlayerReport, StrikeZones } from '../../../../data';
import ReportCard from '../../../../components/ReportCard';
import ReceivingChart from '../../../../components/ReceivingChart';
import VideoPlayer from '../../../../components/VideoPlayer';
import ReceivingGainedLost from './ReceivingGainedLost';
import ReceivingCatchingDepth from './ReceivingCatchingDepth';
import ReceivingGloveLocations from './ReceivingGloveLocations';
import ReceivingRollingAvg from '../../../../components/ReceivingRollingAvg';
import { store } from '../../../../store';

const StyledRow = styled(Row)`
    margin: 10px;
`;
const StyledFiltersContainer = styled.div`
    display: flex;
    justify-content: space-between;
    > div {
        display: flex;
    }
    > div > * {
        margin: 5px;
    }
`;

const buildQueries = (playerid, startdate, enddate, levelid) => {
    const startYearID = moment(startdate).year();
    const endYearID = moment(enddate).year();
    const yearid = startYearID === endYearID ? startYearID : null;
    const queries = [
        {
            queryKey: ['player', playerid],
            queryFn: (t) => Player.get(t, { playerId: playerid }),
        },
        {
            queryKey: ['receivingReport', playerid, startdate, enddate, levelid],
            queryFn: (t) => (yearid
                ? PlayerReport.recv(t, { playerid, yearid, levelid, gainthreshold: 0.5, lostthreshold: 0.5 })
                : PlayerReport.recv(t, { playerid, startdate, enddate, levelid, gainthreshold: 0.5, lostthreshold: 0.5 })
            ),
        },
        {
            queryKey: ['receivingReportDepthLocation', playerid, startdate, enddate, levelid],
            queryFn: (t) => (yearid
                ? PlayerReport.recvdepthlocation(t, { playerid, yearid, levelid })
                : PlayerReport.recvdepthlocation(t, { playerid, startdate, enddate, levelid })
            ),
        },
        {
            queryKey: ['levelavgreceivingReport', { playerid, startdate, enddate, levelid }],
            queryFn: (t) => (yearid
                ? PlayerReport.levelaveragerecv(t, { playerid, yearid, levelid })
                : PlayerReport.levelaveragerecv(t, { playerid, startdate, enddate, levelid })
            ),
        },
        {
            queryKey: ['strikeZoneContoursLhb', playerid, yearid],
            queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: true, yearid, levelid }),
        },
        {
            queryKey: ['strikeZoneContoursRhb', playerid, yearid],
            queryFn: (t) => StrikeZones.getStrikeZoneContours(t, { islhb: false, yearid, levelid }),
        },
    ];
    return queries;
};

const mapDataResponse = (response) => {
    const player = response[0].data;
    const recData = response[1].data;
    const recDepthLocationData = response[2].data;
    const contoursLhb = response[4].data;
    const contoursRhb = response[5].data;
    const { rollingavgpitches, rollingavgh2h } = recData;
    const rollingAvgPitches = rollingavgpitches.map((d) => ({ gamedate: d.gamedate, value: d.ravg_framingstk, split: 'overall' }));
    const rollingAvgH2HData = rollingavgh2h.filter((d) => d.gamedate !== null).map((d) => ({ gamedate: d.gamedate, value: d.h2h, split: 'h2h' }));
    const rollingAvgPitchData = rollingAvgPitches.concat(rollingAvgH2HData);
    return { player, recData, recDepthLocationData, contoursLhb, contoursRhb, rollingAvgPitchData };
};

const filterPitches = (pitchLog, passedBalls, filters) => {
    const { split, result } = filters;
    const filteredPitches = pitchLog.filter((p) => (!split.filterFn || split.filterFn(p)) && (!result.filterFn || result.filterFn(p)));
    const filteredPassedBalls = passedBalls.filter((p) => (!split.filterFn || split.filterFn(p)) && (!result.filterFn || result.filterFn(p)));

    if (result.name === 'Passed Ball') return { filteredPitches: [], filteredPassedBalls };
    return { filteredPitches, filteredPassedBalls };
};

const filterDepthLocations = (depth, depthlgavg, filters) => {
    const { pitchtype } = filters;
    const filteredDepth = depth?.filter((p) => (!pitchtype.filterFn || pitchtype.filterFn(p))) || [];
    const filteredDepthlgavg = depthlgavg?.filter((p) => (!pitchtype.filterFn || pitchtype.filterFn(p))) || [];
    return { filteredDepth, filteredDepthlgavg };
};

const getCatcherVideo = (pitchLog = [], user) => {
    const playlist = mapPlaylistFromVideoSearch({ videos: pitchLog, playervideos: [] }, user.Roles.map((r) => r.RoleName));
    return playlist;
};

const splitOptions = (translate) => [
    { name: translate('sections.reports.catcher.receiving.overall'), filterFn: null },
    { name: translate('sections.reports.catcher.receiving.abbr-run-on-base'), filterFn: (x) => x.runners_on },
    { name: translate('sections.reports.catcher.receiving.empty'), filterFn: (x) => x.bases_empty },
    { name: translate('baseball.pitches.fastball_one'), filterFn: (x) => x.fb },
    { name: translate('baseball.pitches.fastball-non'), filterFn: (x) => x.nonfb },
];

const resultOptions = (translate) => [
    { name: translate('common.all'), filterFn: null },
    { name: `+0.5 ${translate('common.to')} +1.0 (${translate('baseball.strikes_one')})`, filterFn: (x) => x.gained },
    { name: `+0.3 ${translate('common.to')} +0.5 (${translate('baseball.strikes_one')})`, filterFn: (x) => !x.gained && !x.lost && x.isstrike === true },
    { name: `-0.5 ${translate('common.to')} -0.3 (${translate('baseball.balls_one')})`, filterFn: (x) => !x.gained && !x.lost && x.isstrike === false },
    { name: `-1.0 ${translate('common.to')} -0.5 (${translate('baseball.balls_one')})`, filterFn: (x) => x.lost },
    { name: translate('sections.reports.catcher.receiving.passed-ball'), filterFn: () => true },
];

const pitchtypeOptions = (translate) => [
    { name: translate('sections.reports.catcher.receiving.overall'), filterFn: null },
    { name: translate('baseball.pitches.fastball_one'), filterFn: (x) => x.type === 9 || x.type === 10 },
    { name: translate('baseball.pitches.curve'), filterFn: (x) => x.type === 2 },
    { name: translate('baseball.pitches.slider'), filterFn: (x) => x.type === 3 },
    { name: translate('baseball.pitches.changeup_one'), filterFn: (x) => x.type === 4 },
    { name: translate('baseball.pitches.sinker'), filterFn: (x) => x.type === 5 },
    { name: translate('baseball.pitches.splitter'), filterFn: (x) => x.type === 7 },
    { name: translate('baseball.pitches.cutter'), filterFn: (x) => x.type === 8 },
    { name: translate('baseball.pitches.abbr-fastball-2'), filterFn: (x) => x.type === 9 },
    { name: translate('baseball.pitches.abbr-fastball-4'), filterFn: (x) => x.type === 10 },
];

const ReceivingReport = ({ playerID, startYear, endYear, level: levelid }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const [playlist, setPlaylist] = useState([]);
    const [filters, setFilters] = useState({ split: splitOptions(translate)[0], result: resultOptions(translate)[0], pitchtype: pitchtypeOptions(translate)[0] });

    const startdate = moment(`${startYear}-01-01`).format('YYYY-MM-DD');
    const enddate = moment(`${endYear}-12-31`).format('YYYY-MM-DD');

    const dataQueries = buildQueries(playerID, startdate, enddate, levelid);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (dataQueriesResponse.some((q) => q.isFetching)) {
        return <LoadingSpinner />;
    }

    const { player, recData, recDepthLocationData, contoursLhb, contoursRhb, rollingAvgPitchData } = mapDataResponse(dataQueriesResponse);
    const { pitchlog, passedballs } = recData;
    if (pitchlog.length === 0) {
        return <>{translate('sections.reports.catcher.receiving.no-data-filters')}</>;
    }
    const { depth, depthlgavg, glovelocations } = recDepthLocationData;
    const { filteredDepth, filteredDepthlgavg } = filterDepthLocations(depth, depthlgavg, filters);

    /* Video */
    const { filteredPitches, filteredPassedBalls } = filterPitches(pitchlog, passedballs, filters);
    const allVideosPlaylist = getCatcherVideo(filteredPitches, user);

    return (
        <>
            <StyledRow>
                <Col xl={{ span: 5, order: 1 }} md={{ span: 12, order: 2 }}>
                    <ReportCard>
                        <ReceivingChart
                            pitches={filteredPitches}
                            passedBalls={filteredPassedBalls}
                            contoursLhb={contoursLhb}
                            contoursRhb={contoursRhb}
                        />
                        <StyledFiltersContainer>
                            <div>
                                <Dropdown
                                    options={splitOptions(translate)}
                                    value={filters.split}
                                    onSelect={(value) => setFilters({ ...filters, split: value })}
                                />
                                <Dropdown
                                    options={resultOptions(translate)}
                                    value={filters.result}
                                    onSelect={(value) => setFilters({ ...filters, result: value })}
                                />
                            </div>
                            <div>
                                <Button variant="primary" onClick={() => setPlaylist(allVideosPlaylist)}>{translate('sections.reports.catcher.receiving.play-videos')}</Button>
                            </div>
                        </StyledFiltersContainer>
                    </ReportCard>
                </Col>
                <Col xl={{ span: 7, order: 1 }} md={{ span: 12, order: 2 }}>
                    <ReportCard body={!playlist?.length}>
                        {playlist?.length ? <VideoPlayer playlist={playlist} /> : <h4>{translate('sections.reports.catcher.receiving.no-video-selected')}</h4>}
                    </ReportCard>
                </Col>
            </StyledRow>
            <StyledRow>
                <Col sm={{ span: 12 }}>
                    <ReportCard>
                        <ReceivingGainedLost recData={recData} />
                    </ReportCard>
                </Col>
            </StyledRow>
            <StyledRow>
                <Col sm={{ span: 12 }}>
                    <ReportCard title={`${player.UsesName} ${player.LastName} ${translate('sections.reports.catcher.receiving.receiving-dashboard-title')}`}>
                        <ReceivingRollingAvg rollingAvgData={rollingAvgPitchData} minY={-2.5} maxY={2.5} />
                    </ReportCard>
                </Col>
            </StyledRow>
            <StyledRow>
                <Col xl={{ span: 6 }} sm={{ span: 12 }}>
                    <ReportCard>
                        <StyledFiltersContainer>
                            <div>
                                <Dropdown
                                    options={pitchtypeOptions(translate)}
                                    value={filters.pitchtype}
                                    onSelect={(value) => setFilters({ ...filters, pitchtype: value })}
                                />
                            </div>
                        </StyledFiltersContainer>
                        <ReceivingCatchingDepth catcherDepthData={filteredDepth} catcherDepthLevelAvgData={filteredDepthlgavg} />
                    </ReportCard>
                </Col>
                <Col xl={{ span: 6 }} sm={{ span: 12 }}>
                    <ReportCard>
                        <ReceivingGloveLocations gloveLocationsData={glovelocations} />
                    </ReportCard>
                </Col>
            </StyledRow>
        </>
    );
};

ReceivingReport.propTypes = {
    playerID: PropTypes.number.isRequired,
    startYear: PropTypes.number.isRequired,
    endYear: PropTypes.number.isRequired,
    level: PropTypes.number.isRequired,
};

export default ReceivingReport;
